import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../lib/api';
import { getTenantId } from '../../lib/config';
import { ChevronLeft, ChevronRight, Users, Bed } from 'lucide-react';

interface Room {
  id: string;
  roomNumber: string;
  title?: string;
  roomTypeRef?: {
    id: string;
    name: string;
    maxOccupancy?: number;
    bedType?: string;
  };
  basePricePerNight?: number;
  currency?: string;
  maxOccupancy?: number;
  images?: Array<{
    id: string;
    imageUrl: string;
    isPrimary: boolean;
  }>;
}

/**
 * Landing Page Accommodation Section
 * 
 * Airbnb-style horizontal scrolling cards with room prices and info
 */
export default function LandingAccommodationSection() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Determine how many rooms to show on mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const displayedRooms = isMobile && !showAll ? rooms.slice(0, 4) : rooms;

  useEffect(() => {
    const loadRooms = async () => {
      try {
        setLoading(true);
        const tenantId = getTenantId();
        
        // Fetch available rooms from public endpoint
        const response = await fetch(`${apiService['baseUrl']}/api/public/accommodations?tenantId=${tenantId}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch accommodations: ${response.statusText}`);
        }

        const data = await response.json();
        
        if (data.success && data.data) {
          const allRooms = data.data.rooms || [];
          const companies = data.data.companies || [];
          
          // Get the first company ID from the companies array
          const firstCompanyId = companies.length > 0 ? companies[0].id : null;
          
          // Filter rooms by first company and only show active rooms
          const firstCompanyRooms = firstCompanyId 
            ? allRooms.filter((room: any) => room.companyId === firstCompanyId && room.isActive)
            : [];
          
          setRooms(firstCompanyRooms);
        }
      } catch (err: any) {
        console.error("Failed to load rooms:", err);
      } finally {
        setLoading(false);
      }
    };

    loadRooms();
  }, []);

  // Auto-scroll effect
  useEffect(() => {
    if (rooms.length <= 3 || !scrollContainerRef.current) return;

    const autoScroll = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const scrollAmount = 350; // One card width + gap
        const maxScroll = container.scrollWidth - container.clientWidth;
        
        // If we're at the end, scroll back to start
        if (container.scrollLeft >= maxScroll - 10) {
          container.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        } else {
          // Otherwise, scroll to the next card
          container.scrollTo({
            left: container.scrollLeft + scrollAmount,
            behavior: 'smooth'
          });
        }
      }
    };

    // Start auto-scroll after 3 seconds
    autoScrollIntervalRef.current = setInterval(autoScroll, 3000);

    // Pause auto-scroll on hover
    const container = scrollContainerRef.current;
    const pauseAutoScroll = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };

    const resumeAutoScroll = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      autoScrollIntervalRef.current = setInterval(autoScroll, 3000);
    };

    container?.addEventListener('mouseenter', pauseAutoScroll);
    container?.addEventListener('mouseleave', resumeAutoScroll);

    // Cleanup
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      container?.removeEventListener('mouseenter', pauseAutoScroll);
      container?.removeEventListener('mouseleave', resumeAutoScroll);
    };
  }, [rooms.length]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 350;
      const newScrollLeft = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleRoomClick = (roomId: string) => {
    navigate(`/room/${roomId}`);
  };

  const formatCurrency = (amount?: number, currency: string = 'RWF') => {
    if (!amount) return 'N/A';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return `0 ${currency}`;
    
    if (currency === 'RWF' || currency === 'FRW') {
      return `${Math.round(numAmount).toLocaleString('en-US')} FRW`;
    } else {
      return `${currency} ${numAmount.toFixed(2)}`;
    }
  };

  const getRoomImage = (room: Room) => {
    if (room.images && room.images.length > 0) {
      const primaryImage = room.images.find((img) => img.isPrimary) || room.images[0];
      const imageUrl = primaryImage.imageUrl?.startsWith('http') 
        ? primaryImage.imageUrl 
        : primaryImage.imageUrl?.startsWith('/')
        ? `${apiService['baseUrl']}${primaryImage.imageUrl}`
        : `${apiService['baseUrl']}/${primaryImage.imageUrl}`;
      return imageUrl;
    }
    return '/images/webflow/LOB3.jpg';
  };

  return (
    <div className="w-full py-12 bg-white">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ color: '#2C2416' }}>
            Accommodation
          </h2>
          
          {/* Navigation Arrows - Hidden on mobile, shown on larger screens */}
          {rooms.length > 3 && (
            <div className="hidden sm:flex gap-2">
              <button
                onClick={() => scroll('left')}
                className="p-2 rounded-full border border-gray-300 hover:border-gray-400 transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => scroll('right')}
                className="p-2 rounded-full border border-gray-300 hover:border-gray-400 transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading rooms...</p>
            </div>
          </div>
        )}

        {/* Room Cards - Grid Layout on Mobile, Horizontal Scroll on Desktop */}
        {!loading && rooms.length > 0 && (
          <>
            <div
              ref={scrollContainerRef}
              className="hidden sm:flex gap-4 sm:gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {rooms.map((room) => (
                <div
                  key={room.id}
                  onClick={() => handleRoomClick(room.id)}
                  className="flex-shrink-0 w-[320px] md:w-[340px] cursor-pointer group"
                >
                  {/* Room Card - Image with Info Overlay at Bottom */}
                  <div className="relative h-[380px] rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    {/* Room Image */}
                    <img
                      src={getRoomImage(room)}
                      alt={room.roomNumber}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/images/webflow/LOB3.jpg';
                      }}
                    />

                    {/* Gradient Overlay at Bottom */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                    {/* Room Info - Positioned at Bottom with Transparent Background */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
                      <h3 className="font-semibold text-lg text-white truncate">
                        {room.roomNumber}
                      </h3>
                      
                      {/* Room Details */}
                      <div className="flex items-center gap-3 text-sm text-white/90">
                        {(room.maxOccupancy || room.roomTypeRef?.maxOccupancy) && (
                          <div className="flex items-center gap-1">
                            <Users size={16} />
                            <span>{room.maxOccupancy || room.roomTypeRef?.maxOccupancy} guests</span>
                          </div>
                        )}
                        {room.roomTypeRef?.bedType && (
                          <div className="flex items-center gap-1">
                            <Bed size={16} />
                            <span>{room.roomTypeRef.bedType}</span>
                          </div>
                        )}
                      </div>

                      {/* Price */}
                      <div className="pt-1">
                        <span className="font-semibold text-xl text-white">
                          {formatCurrency(room.basePricePerNight, room.currency || 'RWF')}
                        </span>
                        <span className="text-white/90 text-sm"> / night</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Grid Layout */}
            <div className="sm:hidden grid grid-cols-1 gap-4">
              {displayedRooms.map((room) => (
                <div
                  key={room.id}
                  onClick={() => handleRoomClick(room.id)}
                  className="cursor-pointer group"
                >
                  {/* Room Card - Image with Info Overlay at Bottom */}
                  <div className="relative h-[340px] rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    {/* Room Image */}
                    <img
                      src={getRoomImage(room)}
                      alt={room.roomNumber}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/images/webflow/LOB3.jpg';
                      }}
                    />

                    {/* Gradient Overlay at Bottom */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                    {/* Room Info - Positioned at Bottom with Transparent Background */}
                    <div className="absolute bottom-0 left-0 right-0 p-3 space-y-1.5">
                      <h3 className="font-semibold text-base text-white truncate">
                        {room.roomNumber}
                      </h3>
                      
                      {/* Room Details */}
                      <div className="flex items-center gap-2 text-xs text-white/90">
                        {(room.maxOccupancy || room.roomTypeRef?.maxOccupancy) && (
                          <div className="flex items-center gap-1">
                            <Users size={14} />
                            <span>{room.maxOccupancy || room.roomTypeRef?.maxOccupancy} guests</span>
                          </div>
                        )}
                        {room.roomTypeRef?.bedType && (
                          <div className="flex items-center gap-1">
                            <Bed size={14} />
                            <span>{room.roomTypeRef.bedType}</span>
                          </div>
                        )}
                      </div>

                      {/* Price */}
                      <div className="pt-0.5">
                        <span className="font-semibold text-lg text-white">
                          {formatCurrency(room.basePricePerNight, room.currency || 'RWF')}
                        </span>
                        <span className="text-white/90 text-xs"> / night</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Button - Mobile Only */}
            {isMobile && !showAll && rooms.length > 4 && (
              <div className="sm:hidden mt-6 text-center">
                <button
                  onClick={() => setShowAll(true)}
                  className="px-8 py-3 rounded-lg font-semibold text-white transition-all hover:scale-105"
                  style={{ backgroundColor: '#f5945c' }}
                >
                  Load More ({rooms.length - 4} more rooms)
                </button>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!loading && rooms.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg">No rooms available at the moment</p>
          </div>
        )}
      </div>
    </div>
  );
}

